@use "@/styles/variables" as *;

.container {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 2px;
	min-width: 0;
}

.heroCard {
	position: relative;
	border: 1px solid $gray-200;
	border-radius: 12px;
	padding: 14px 16px;
	background: linear-gradient(145deg, #ffffff 0%, #fcfdf8 65%, #f8fbf0 100%);
	overflow: hidden;

	&::after {
		content: "";
		position: absolute;
		right: -30px;
		top: -35px;
		width: 120px;
		height: 120px;
		border-radius: 999px;
		background: radial-gradient(circle, rgba(127, 176, 47, 0.22) 0%, rgba(127, 176, 47, 0.02) 65%, transparent 100%);
		pointer-events: none;
	}
}

.heroBadge {
	display: inline-flex;
	align-items: center;
	font-size: 0.6875rem;
	font-weight: 700;
	color: $primary-color;
	background: rgba(127, 176, 47, 0.12);
	border: 1px solid rgba(127, 176, 47, 0.25);
	border-radius: 999px;
	padding: 4px 8px;
	margin-bottom: 8px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.heroTitle {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 700;
	color: $gray-700;
	line-height: 1.3;
}

.heroMetaGrid {
	margin-top: 12px;
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 10px;
}

.heroMetaItem {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 8px 10px;
	border: 1px solid $gray-100;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.75);
}

.heroMetaLabel {
	font-size: 0.6875rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: $gray-500;
}

.heroMetaValue {
	font-size: 0.85rem;
	font-weight: 600;
	line-height: 1.3;
	color: $gray-700;
	word-break: break-word;
}

.responseCard {
	background: linear-gradient(180deg, #ffffff 0%, $gray-50 100%);
	border: 1px solid $gray-200;
	border-radius: $border-radius;
	padding: 11px 13px;
	min-width: 0;
	overflow: hidden;
}

.responseTitle {
	font-size: 0.8125rem;
	font-weight: 700;
	margin: 0 0 6px;
	color: $gray-700;
	letter-spacing: 0.01em;
}

.responseText {
	font-size: 0.875rem;
	line-height: 1.35;
	margin: 0;
	color: $danger-color;
	max-width: 100%;
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	word-break: break-word;
}

.payloadCard {
	border: 1px solid $gray-200;
	border-radius: 12px;
	padding: 12px;
	background: linear-gradient(180deg, #ffffff 0%, $gray-50 100%);
	box-shadow: 0 14px 30px -24px rgba(17, 24, 39, 0.5);
	min-width: 0;
	overflow: hidden;
}

.payloadTitle {
	margin: 0 0 8px;
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: $gray-600;
}

.payloadContent {
	margin: 0;
	width: 100%;
	max-width: 100%;
	padding: 12px;
	max-height: 240px;
	overflow-y: auto;
	overflow-x: hidden;
	border: 1px solid $gray-200;
	border-radius: 10px;
	background: #111827;
	color: #f9fafb;
	font-size: 0.75rem;
	line-height: 1.45;
	font-family: "Consolas", "Monaco", monospace;
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	word-break: break-word;
	box-sizing: border-box;
}

.codesCard,
.fieldsCard {
	border: 1px solid $gray-200;
	border-radius: 12px;
	padding: 14px;
	background: #ffffff;
}

.codesLabel {
	font-size: 0.875rem;
	font-weight: 700;
	margin: 0 0 10px;
	display: block;
	color: $gray-700;
	letter-spacing: 0.01em;
}

.externalCodesContainer {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.inputRow {
	display: flex;
	gap: 8px;
	align-items: center;
}

.codeInput {
	flex: 1;
	padding: 9px 11px;
	border: 1px solid $gray-200;
	border-radius: 10px;
	font-size: 0.875rem;
	min-height: 36px;
	background: #ffffff;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;

	&:focus {
		outline: none;
		border-color: $primary-color;
		box-shadow: 0 0 0 3px rgba(130, 188, 68, 0.16);
	}
}

.codeStatus {
	font-size: 0.75rem;
	color: $gray-600;
	background: $gray-50;
	border: 1px solid $gray-100;
	padding: 8px 10px;
	border-radius: 10px;
}

.codesTableWrapper {
	overflow-x: auto;
	border: 1px solid $gray-200;
	border-radius: $border-radius;
	background: #ffffff;
}

.codesTable {
	width: 100%;
	font-size: 0.875rem;
	border-collapse: collapse;
	background: #ffffff;
}

.codeHeaderCell {
	padding: 8px;
	text-align: left;
	font-size: 0.75rem;
	font-weight: 700;
	color: $gray-700;
	border-bottom: 1px solid $gray-100;
	background: $gray-50;
}

.codeRow {
	border-bottom: 1px solid $gray-50;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: rgba(127, 176, 47, 0.06);
	}

	&.selected {
		background-color: rgba(127, 176, 47, 0.18);
	}
}

.codeCell {
	padding: 6px 8px;
	font-weight: 500;
	min-width: 180px;
}

.actionCell {
	padding: 6px 8px;
	text-align: center;
	width: 130px;
}

.deleteCell {
	padding: 6px 8px;
	text-align: center;
	width: 120px;
}

.fieldsContainer {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.fieldsGrid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
}

.fieldsCard {
	position: relative;

	&::before {
		content: "";
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 3px;
		border-top-left-radius: $border-radius;
		border-bottom-left-radius: $border-radius;
		background: linear-gradient(180deg, $primary-color 0%, $secondary-color 100%);
	}
}

@media (max-width: 768px) {
	.heroMetaGrid {
		grid-template-columns: 1fr;
	}

	.inputRow {
		flex-direction: column;
		align-items: stretch;
	}

	.fieldsGrid {
		grid-template-columns: 1fr;
	}
}
